import { showHttpError } from '@calendly/alert'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

import {
  filterSelector,
  sortFieldSelector,
  sortOrderSelector,
} from './selectors'
import { getInvitationMemberships } from '../../api'
import { hasRoleInGroup } from '../helpers/has_role_in_group'


const initialState = {
  status: 'loading',
  members: {
    entities: [],
    pagination: {
      currentPage: 1,
      totalPages: 1,
      totalCount: 0
    },
    filter: '',
    isLoading: true,
    sortField: 'email',
    sortOrder: 'asc',
    selectedMembers: []
  },
  removePopup: {
    members: [],
    isOpen: false,
    pending: false,
    role: null
  }
}

export const fetchMigrationMemberships = createAsyncThunk(
  'userGroupInvitationMembers/fetch',
  async ({ groupId, page }, { dispatch, getState, rejectWithValue }) => {
    const state = getState()
    const filter = filterSelector(state)
    const sortField = sortFieldSelector(state)
    const sortOrder = sortOrderSelector(state)
    const [success, response] = await getInvitationMemberships(groupId, { page, filter, sortField, sortOrder, status: 'migrating' })

    if (!success) {
      dispatch(showHttpError(response))
      return rejectWithValue([])
    }

    return response
  }
)

export const slice = createSlice({
  name: 'userGroup/migrationMemberships',
  initialState,
  reducers: {
    loaded(state) {
      state.status = 'loaded'
    },
    startDataLoad(state) {
      state.members.entities = []
      state.members.isLoading = true
    },
    filterUpdated(state, action) {
      state.members.filter = action.payload
    },
    openRemovePopup(state, action) {
      state.removePopup.isOpen = true
      state.removePopup.members = [action.payload]
      state.removePopup.pending = initialState.removePopup.pending
    },
    closeRemovePopup(state) {
      state.removePopup = { ...initialState.removePopup }
    },
    openBatchRemovePopup(state, action) {
      state.removePopup.isOpen = true
      state.removePopup.members = state.members.selectedMembers
      state.removePopup.role = action?.payload
    },
    reset: () => initialState,
    toggleSelection: (state, { payload: member }) => {
      const { selectedMembers } = state.members
      if (selectedMembers.some(({ id }) => id === member.id)) {
        state.members.selectedMembers = selectedMembers.filter(({ id }) => id !== member.id)
      } else {
        state.members.selectedMembers.push(member)
      }
    },
    togglePageSelection: (state, { payload }) => {
      const { entities, selectedMembers } = state.members
      const selectedIds = Object.fromEntries(selectedMembers.map(({ id }) => [id, true]))
      const membershipIds = Object.fromEntries(entities.map(({ id }) => [id, true]))
      const remainingMembers = entities
        .filter((member) => !selectedIds[member.id] && hasRoleInGroup(member, payload.role, payload.userGroup))

      if (remainingMembers.length) {
        state.members.selectedMembers.push(...remainingMembers)
      } else {
        state.members.selectedMembers = selectedMembers.filter(({ id }) => !membershipIds[id])
      }
    },
    clearSelection: (state) => {
      state.members.selectedMembers = []
    },
    setFilter(state, { payload }) {
      state.members.filter = payload
    },
    setSortField(state, { payload }) {
      state.members.sortField = payload
    },
    setSortOrder(state, { payload }) {
      state.members.sortOrder = payload
    },
    setPage: (state, { payload }) => {
      state.members.pagination.currentPage = payload
    },
    setRemovePopupPending: (state, { payload }) => {
      state.removePopup.pending = payload
    },
    setRemovePopupRole: (state, { payload }) => {
      state.removePopup.role = payload
    },
    sortBy(state, { payload }) {
      if (state.members.sortField === payload) {
        state.members.sortOrder = state.members.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        state.members.sortOrder = 'asc'
      }

      state.members.sortField = payload
    }
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMigrationMemberships.fulfilled, (state, { payload }) => {
      state.members.entities = payload.results
      state.members.pagination = {
        currentPage: payload.pagination.current_page,
        totalPages: payload.pagination.total_pages,
        totalCount: payload.pagination.total_count,
        pageSize: payload.pagination.page_size
      }
      state.members.isLoading = false
    })
    builder.addCase(fetchMigrationMemberships.rejected, (state) => {
      state.status = 'failed'
      state.members.entities = []
      state.members.isLoading = false
    })
  }
})

export const {
  closeRemovePopup, openBatchRemovePopup, openRemovePopup, setRemovePopupRole,
  toggleSelection, clearSelection, togglePageSelection
} = slice.actions

export const { reducer } = slice
